import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  getSavedContext,
  saveContext,
  clearSavedContext,
  clearOtherModuleContexts,
  getActiveModuleGroup,
  setActiveModuleGroup,
  getModuleGroup,
  getUrlParams,
  updateUrlParams,
  isPageReload,
  markInitialDocLoadComplete,
} from "../helpers/moduleContext";

/**
 * Custom hook to preserve user context (status, pagination page, search text, active tab)
 * across page refreshes, navigation (view details -> back), and action completions,
 * while automatically resetting to the default status tab when navigating from one module to another.
 */
export default function useModuleContext({
  moduleKey,
  isMultiStatus = true,
  defaultTab = null,
  allowedStatusIds = [],
  defaultStatus = null,
  paramPrefix = "",
}) {
  const isInitialMount = useRef(true);
  const wasReload = useRef(isPageReload());

  const statusParam = paramPrefix ? `${paramPrefix}_status` : "status";
  const pageParam = paramPrefix ? `${paramPrefix}_page` : "page";
  const searchParam = paramPrefix ? `${paramPrefix}_search` : "search";
  const tabParam = paramPrefix ? `${paramPrefix}_tab` : "tab";

  const currentGroup = getModuleGroup(moduleKey);

  // Detect inter-module switch (navigating from a different module group)
  const isModuleSwitch = useMemo(() => {
    const lastActive = getActiveModuleGroup();
    return Boolean(lastActive && currentGroup && lastActive !== currentGroup);
  }, [currentGroup]);

  // Mark initial document load complete shortly after mount so in-app navigations retain context
  useEffect(() => {
    const timer = setTimeout(() => {
      markInitialDocLoadComplete();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle inter-module switch cleanup and record active module group
  useEffect(() => {
    if (isModuleSwitch) {
      clearOtherModuleContexts(currentGroup);
      clearSavedContext(moduleKey);
      if (typeof window !== "undefined" && window.location?.search) {
        try {
          window.history.replaceState(null, "", window.location.pathname);
        } catch (e) {}
      }
    } else if (wasReload.current) {
      clearSavedContext(moduleKey);
      updateUrlParams({ [statusParam]: null, [pageParam]: null });
    }
    if (currentGroup) {
      setActiveModuleGroup(currentGroup);
    }
  }, [isModuleSwitch, currentGroup, moduleKey, statusParam, pageParam]);

  // Initialize status from URL or sessionStorage (or default if module switch or page reload)
  const [selectedStatus, setSelectedStatus] = useState(() => {
    if (isModuleSwitch || wasReload.current) {
      return defaultStatus;
    }
    const urlParams = getUrlParams();
    const urlStatus = urlParams[statusParam];
    if (urlStatus) {
      const parsed = isMultiStatus
        ? urlStatus.split(",").map((s) => s.trim()).filter(Boolean)
        : urlStatus.trim();
      if (isMultiStatus ? parsed.length > 0 : Boolean(parsed)) {
        return parsed;
      }
    }
    const saved = getSavedContext(moduleKey);
    if (saved?.status !== undefined && saved?.status !== null) {
      if (isMultiStatus) {
        if (Array.isArray(saved.status) && saved.status.length > 0) {
          return saved.status;
        }
      } else if (saved.status) {
        return saved.status;
      }
    }
    return defaultStatus;
  });

  // Initialize page from URL or sessionStorage (or 1 if module switch or page reload)
  const [currentPage, setCurrentPage] = useState(() => {
    if (isModuleSwitch || wasReload.current) {
      return 1;
    }
    const urlParams = getUrlParams();
    const urlPage = urlParams[pageParam];
    if (urlPage) {
      const p = parseInt(urlPage, 10);
      if (!isNaN(p) && p > 0) return p;
    }
    const saved = getSavedContext(moduleKey);
    if (saved?.page) {
      const p = parseInt(saved.page, 10);
      if (!isNaN(p) && p > 0) return p;
    }
    return 1;
  });

  // Initialize search from URL or sessionStorage (or "" if module switch)
  const [searchText, setSearchText] = useState(() => {
    if (isModuleSwitch) {
      return "";
    }
    const urlParams = getUrlParams();
    const urlSearch = urlParams[searchParam];
    if (urlSearch !== undefined) return urlSearch;
    const saved = getSavedContext(moduleKey);
    if (saved?.search !== undefined) return saved.search;
    return "";
  });

  // Initialize tab from URL or sessionStorage (or defaultTab if module switch)
  const [activeTab, setActiveTab] = useState(() => {
    if (isModuleSwitch) {
      return defaultTab;
    }
    const urlParams = getUrlParams();
    const urlTab = urlParams[tabParam];
    if (urlTab) return urlTab;
    const saved = getSavedContext(moduleKey);
    if (saved?.tab) return saved.tab;
    return defaultTab;
  });

  // On mount, if we restored from sessionStorage but URL didn't have params, sync URL
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!isModuleSwitch) {
        const paramsToSync = {};
        if (selectedStatus) paramsToSync[statusParam] = selectedStatus;
        if (currentPage > 1) paramsToSync[pageParam] = currentPage;
        if (searchText) paramsToSync[searchParam] = searchText;
        if (activeTab) paramsToSync[tabParam] = activeTab;
        if (Object.keys(paramsToSync).length > 0) {
          updateUrlParams(paramsToSync);
          saveContext(moduleKey, paramsToSync);
        }
      }
    }
  }, [
    isModuleSwitch,
    moduleKey,
    selectedStatus,
    currentPage,
    searchText,
    activeTab,
    statusParam,
    pageParam,
    searchParam,
    tabParam,
  ]);

  // Listen to popstate (e.g. browser back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      const params = getUrlParams();
      const popStatus = params[statusParam];
      if (popStatus) {
        const parsed = isMultiStatus
          ? popStatus.split(",").map((s) => s.trim()).filter(Boolean)
          : popStatus.trim();
        setSelectedStatus(parsed);
      }
      const popPage = params[pageParam];
      if (popPage) {
        const p = parseInt(popPage, 10);
        if (!isNaN(p) && p > 0) setCurrentPage(p);
      } else {
        setCurrentPage(1);
      }
      const popSearch = params[searchParam];
      if (popSearch !== undefined) {
        setSearchText(popSearch);
      } else {
        setSearchText("");
      }
      const popTab = params[tabParam];
      if (popTab) {
        setActiveTab(popTab);
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  }, [isMultiStatus, statusParam, pageParam, searchParam, tabParam]);

  // Handle status change
  const handleStatusChange = useCallback(
    (newStatus) => {
      setSelectedStatus(newStatus);
      setCurrentPage(1);
      saveContext(moduleKey, { status: newStatus, page: 1 });
      updateUrlParams({ [statusParam]: newStatus, [pageParam]: 1 });
    },
    [moduleKey, statusParam, pageParam]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage) => {
      setCurrentPage(newPage);
      saveContext(moduleKey, { page: newPage });
      updateUrlParams({ [pageParam]: newPage });
    },
    [moduleKey, pageParam]
  );

  // Handle search change
  const handleSearchChange = useCallback(
    (newSearch) => {
      setSearchText(newSearch);
      setCurrentPage(1);
      saveContext(moduleKey, { search: newSearch, page: 1 });
      updateUrlParams({ [searchParam]: newSearch, [pageParam]: 1 });
    },
    [moduleKey, searchParam, pageParam]
  );

  // Handle tab change
  const handleTabChange = useCallback(
    (newTab) => {
      setActiveTab(newTab);
      saveContext(moduleKey, { tab: newTab });
      updateUrlParams({ [tabParam]: newTab });
    },
    [moduleKey, tabParam]
  );

  /**
   * Called when status list API completes to supply default status if none is set or valid,
   * or when switching back from another module.
   */
  const applyDefaultStatus = useCallback(
    (fallbackStatus) => {
      setSelectedStatus((current) => {
        // If an inter-module switch just occurred or page was reloaded, always apply fallbackStatus (default status tab)
        if (isModuleSwitch || wasReload.current) {
          wasReload.current = false;
          saveContext(moduleKey, { status: fallbackStatus, page: 1 });
          updateUrlParams({ [statusParam]: fallbackStatus, [pageParam]: null });
          return fallbackStatus;
        }

        // Check if current is already valid and allowed
        const hasValue =
          current !== null &&
          current !== undefined &&
          (Array.isArray(current) ? current.length > 0 : Boolean(current));

        if (hasValue) {
          if (Array.isArray(allowedStatusIds) && allowedStatusIds.length > 0) {
            const isAllowed = Array.isArray(current)
              ? current.some((id) => allowedStatusIds.includes(id))
              : allowedStatusIds.includes(current);

            if (isAllowed) {
              // Valid! Keep current, but ensure storage and URL have it
              saveContext(moduleKey, { status: current });
              updateUrlParams({ [statusParam]: current });
              return current;
            }
          } else {
            // No allowedStatusIds restriction, keep current
            saveContext(moduleKey, { status: current });
            updateUrlParams({ [statusParam]: current });
            return current;
          }
        }

        // Otherwise apply fallback
        saveContext(moduleKey, { status: fallbackStatus, page: 1 });
        updateUrlParams({ [statusParam]: fallbackStatus, [pageParam]: null });
        return fallbackStatus;
      });
    },
    [moduleKey, allowedStatusIds, statusParam, pageParam, isModuleSwitch]
  );

  return {
    selectedStatus,
    setSelectedStatus,
    handleStatusChange,
    currentPage,
    setCurrentPage,
    handlePageChange,
    searchText,
    setSearchText,
    handleSearchChange,
    activeTab,
    setActiveTab,
    handleTabChange,
    applyDefaultStatus,
    pagination: {
      current: currentPage,
      onChange: handlePageChange,
    },
  };
}
